import { useEffect, useState } from 'react';
import { NavLink, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { api, ensureCsrf } from '../../lib/api';

// Gestionalino dei progetti. Chi ha il ruolo «progetti» vede solo questo; il
// server gli chiude tutto il resto (App\Http\Middleware\SoloProgetti).
// La home è la lista dei progetti con il piano operativo aperto dentro la pagina;
// le altre voci sono vuote apposta: i contenuti li decide Michael (20/10/2026).
export const VOCI_PROGETTI = [
  { path: 'progetti', label: 'Progetti' },
  { path: 'algoritmo', label: 'Algoritmo' },
  { path: 'knowledge', label: 'Knowledge' },
  { path: 'video', label: 'Video' },
  { path: 'documenti', label: 'Documenti' },
  { path: 'compiti', label: 'Compiti' },
  { path: 'scadenze', label: 'Scadenze' },
];

function Vuota({ titolo }) {
  return (
    <div style={{ padding: '40px 48px' }}>
      <h1 style={{ fontSize: 24, margin: '0 0 8px' }}>{titolo}</h1>
      <p style={{ color: 'var(--fg-muted)', margin: 0 }}>Sezione ancora vuota.</p>
    </div>
  );
}

// Home: elenco dei progetti (da /api/progetti) e il documento del progetto scelto.
// Diagramma di flusso (mermaid) letto dal campo «flusso» del progetto: si cambia nei dati, senza build.
let mermaidPronto = null;
function caricaMermaid() {
  if (!mermaidPronto) {
    mermaidPronto = import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs').then((m) => {
      m.default.initialize({ startOnLoad: false, theme: 'neutral', flowchart: { useMaxWidth: false, htmlLabels: true } });
      return m.default;
    });
  }
  return mermaidPronto;
}

function Flusso({ sorgente, id }) {
  const [svg, setSvg] = useState('');
  const [errore, setErrore] = useState(null);
  useEffect(() => {
    let vivo = true;
    setSvg('');
    caricaMermaid()
      .then((mermaid) => mermaid.render(`flusso-${id}-${Date.now()}`, sorgente))
      .then((r) => vivo && setSvg(r.svg))
      .catch((e) => vivo && setErrore(String(e?.message || e)));
    return () => { vivo = false; };
  }, [sorgente, id]);
  if (errore) return <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Diagramma non disponibile ({errore}).</div>;
  if (!svg) return <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Disegno il flusso…</div>;
  return <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: '#fff' }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

// Diagramma BPMN (bpmn-js, solo visualizzazione) letto dal campo «bpmn» del progetto.
let bpmnPronto = null;
function caricaBpmn() {
  if (!bpmnPronto) {
    bpmnPronto = new Promise((ok, ko) => {
      for (const href of ['https://cdn.jsdelivr.net/npm/bpmn-js@17.11.1/dist/assets/diagram-js.css', 'https://cdn.jsdelivr.net/npm/bpmn-js@17.11.1/dist/assets/bpmn-font/css/bpmn.css']) {
        const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; document.head.appendChild(l);
      }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/bpmn-js@17.11.1/dist/bpmn-navigated-viewer.production.min.js';
      s.onload = () => ok(window.BpmnJS); s.onerror = () => ko(new Error('bpmn-js non caricato'));
      document.head.appendChild(s);
    });
  }
  return bpmnPronto;
}

function Bpmn({ xml }) {
  const [el, setEl] = useState(null);
  const [errore, setErrore] = useState(null);
  useEffect(() => {
    if (!el) return undefined;
    let viewer = null; let vivo = true;
    caricaBpmn().then((BpmnJS) => {
      if (!vivo) return;
      viewer = new BpmnJS({ container: el });
      return viewer.importXML(xml).then(() => viewer.get('canvas').zoom('fit-viewport', 'auto'));
    }).catch((e) => vivo && setErrore(String(e?.message || e)));
    return () => { vivo = false; viewer?.destroy(); };
  }, [el, xml]);
  if (errore) return <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Diagramma non disponibile ({errore}).</div>;
  return (
    <div>
      <div ref={setEl} style={{ height: 560, border: '1px solid var(--border)', borderRadius: 10, background: '#fff' }} />
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>Trascina per spostarti, rotella per zoomare.</div>
    </div>
  );
}

// Sezione chiudibile della pagina progetto: parte chiusa (solo il titolo) e il contenuto si monta solo quando la apri.
function Sezione({ titolo, children }) {
  const [aperta, setAperta] = useState(false);
  return (
    <details open={aperta} onToggle={(e) => setAperta(e.currentTarget.open)} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
      <summary style={{ fontWeight: 600, cursor: 'pointer' }}>{titolo}</summary>
      {aperta && <div style={{ marginTop: 8 }}>{children}</div>}
    </details>
  );
}

// Etichetta «puoi farlo tu»: cose che Michael (IKIGAI) può fare senza il geometra (22/10/2026).
function Tu() {
  return <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#2f7d4f', borderRadius: 999, padding: '2px 8px', marginLeft: 8, whiteSpace: 'nowrap' }}>puoi farlo tu</span>;
}

// Scheda di un contatore (foto, stato, dati letti dal video).
function Contatore({ c }) {
  const col = { rosso: '#b42318', verde: '#2f7d4f', ambra: '#a6701c' }[c.colore] || 'var(--fg-muted)';
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', maxWidth: 360, marginBottom: 10 }}>
      {c.foto && <img src={c.foto} alt={`Contatore ${c.nome}`} style={{ display: 'block', width: '100%', aspectRatio: '4/3', objectFit: 'cover' }} />}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <strong>{c.nome}</strong>
          <span style={{ fontSize: 12, fontWeight: 700, color: col, border: `1px solid ${col}`, borderRadius: 999, padding: '2px 8px' }}>{c.stato}</span>
        </div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', margin: '8px 0 0', fontSize: 13 }}>
          {Object.entries(c.dati || {}).map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt style={{ color: 'var(--fg-muted)' }}>{k}</dt><dd style={{ margin: 0 }}>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// Difformità urbanistiche da togliere (campo «difformita» del progetto): foto dalla cartella privata, costi a confronto.
function Difformita({ d, slug }) {
  const cella = { padding: '6px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  return (
    <Sezione titolo={d.titolo || 'Difformità'}>
      {d.nota && <div style={{ fontSize: 14, marginBottom: 10 }}>{d.nota}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {(d.voci || []).map((v) => {
          const col = { rosso: '#b42318', verde: '#2f7d4f', ambra: '#a6701c' }[v.colore] || 'var(--fg-muted)';
          return (
            <div key={v.nome} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, (v.foto || []).length)}, 1fr)`, gap: 2 }}>
                {(v.foto || []).map((f) => (
                  <a key={f.file} href={`/progetti/${slug}/${f.file}`} target="_blank" rel="noreferrer" title={f.didascalia}>
                    <img src={`/progetti/${slug}/${f.file}`} alt={f.didascalia} style={{ display: 'block', width: '100%', aspectRatio: '9/16', objectFit: 'cover' }} />
                  </a>
                ))}
              </div>
              <div style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <strong>{v.nome}</strong>
                  <span style={{ fontSize: 12, fontWeight: 700, color: col, border: `1px solid ${col}`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{v.stato}</span>
                </div>
                {v.descrizione && <div style={{ fontSize: 13, margin: '6px 0 0' }}>{v.descrizione}</div>}
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', margin: '8px 0 0', fontSize: 13 }}>
                  {Object.entries(v.dati || {}).map(([k, x]) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <dt style={{ color: 'var(--fg-muted)' }}>{k}</dt><dd style={{ margin: 0 }}>{x}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          );
        })}
      </div>
      {Array.isArray(d.costi) && (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {['Voce', d.colonne?.[0] || 'CTU', d.colonne?.[1] || 'Nostra stima'].map((h, i) => (
                  <th key={h} style={{ ...cella, textAlign: i ? 'right' : 'left', color: 'var(--fg-muted)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.costi.map((r) => (
                <tr key={r.voce} style={r.totale ? { fontWeight: 700 } : undefined}>
                  <td style={cella}>{r.voce}</td>
                  <td style={{ ...cella, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.ctu}</td>
                  <td style={{ ...cella, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.nostra}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.avviso && <div style={{ fontSize: 13, marginTop: 10, padding: '8px 12px', borderLeft: '4px solid #a6701c', background: 'rgba(166,112,28,.08)' }}>{d.avviso}</div>}
    </Sezione>
  );
}

// Liste di cose da fare per interlocutore (campo «liste» del progetto): una nuova lista è un dato, non codice.
function Lista({ l }) {
  return (
    <Sezione titolo={l.titolo}>
      {l.nota && <div style={{ fontSize: 14, marginBottom: 8 }}>{l.nota}</div>}
      <ol style={{ margin: 0, paddingLeft: 22, fontSize: 14, lineHeight: 1.5 }}>
        {(l.punti || []).map((p) => {
          const col = { rosso: '#b42318', verde: '#2f7d4f', ambra: '#a6701c' }[p.colore] || 'var(--fg-muted)';
          return (
            <li key={p.titolo} style={{ marginBottom: 8 }}>
              <strong>{p.titolo}</strong>
              {p.stato && <span style={{ fontSize: 12, fontWeight: 700, color: col, border: `1px solid ${col}`, borderRadius: 999, padding: '1px 8px', marginLeft: 8, whiteSpace: 'nowrap' }}>{p.stato}</span>}
              {p.tu && <Tu />}
              {p.testo && <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{p.testo}</div>}
            </li>
          );
        })}
      </ol>
    </Sezione>
  );
}

// Spese del progetto (Michael, 24/10/2026): ogni uscita con data, voce e importo, e il totale.
// L'importo può restare vuoto finché non si sa la cifra: la riga resta in evidenza come «da completare».
const euro = (n) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', useGrouping: 'always' });
const oggi = () => new Date().toISOString().slice(0, 10);
const VUOTA = { data: '', voce: '', categoria: 'Altro', importo: '', note: '', pagata_da: 'Michael Sermoneta' };
// Importi scritti all'italiana: «1.500» sono millecinquecento, «1.234,50» ha la virgola per i decimali.
// Restituisce null se vuoto, NaN se illeggibile (il modulo allora non salva).
function leggiImporto(testo) {
  const t = String(testo).replace(/[€\s]/g, '');
  if (t === '') return null;
  if (t.includes(',')) return /^\d{1,3}(\.\d{3})*,\d{1,2}$|^\d+,\d{1,2}$/.test(t) ? Number(t.replace(/\./g, '').replace(',', '.')) : NaN;
  if (/^\d{1,3}(\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ''));
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : NaN;
}

function Spese({ slug }) {
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState(null);
  const [form, setForm] = useState({ ...VUOTA, data: oggi() });
  const [inModifica, setInModifica] = useState(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    api.get(`/api/progetti/${slug}/spese`).then((r) => setD(r.data)).catch((e) => setErrore(e.message));
  }, [slug]);

  const salva = async (e) => {
    e.preventDefault();
    const importo = leggiImporto(form.importo);
    if (Number.isNaN(importo)) {
      setErrore(`Importo non valido: «${form.importo}». Scrivi per esempio 1500 oppure 1.500,50.`);
      return;
    }
    setSalvo(true);
    setErrore(null);
    try {
      await ensureCsrf();
      const corpo = { ...form, importo };
      const r = inModifica
        ? await api.put(`/api/progetti/${slug}/spese/${inModifica}`, corpo)
        : await api.post(`/api/progetti/${slug}/spese`, corpo);
      setD(r.data);
      setForm({ ...VUOTA, data: oggi() });
      setInModifica(null);
    } catch (err) {
      setErrore(err.response?.data?.message || err.message);
    } finally {
      setSalvo(false);
    }
  };

  const elimina = async (v) => {
    if (!window.confirm(`Togliere «${v.voce}»?`)) return;
    await ensureCsrf();
    const r = await api.delete(`/api/progetti/${slug}/spese/${v.id}`);
    setD(r.data);
  };

  const modifica = (v) => {
    setInModifica(v.id);
    setForm({ data: v.data, voce: v.voce, categoria: v.categoria, importo: v.importo == null ? '' : String(v.importo).replace('.', ','), note: v.note ?? '', pagata_da: v.pagata_da ?? '' });
  };

  if (errore && !d) return <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Non riesco a leggere le spese ({errore}).</div>;
  if (!d) return <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Caricamento…</div>;

  const perCategoria = d.categorie
    .map((c) => [c, d.voci.filter((v) => v.categoria === c).reduce((s, v) => s + (v.importo || 0), 0)])
    .filter(([, t]) => t > 0);
  // Chi ha pagato cosa: Michael di tasca sua contro la società (Michael, 24/10/2026).
  const perPagatore = [...new Set(d.voci.map((v) => v.pagata_da || 'non indicato'))]
    .map((n) => [n, d.voci.filter((v) => (v.pagata_da || 'non indicato') === n).reduce((s, v) => s + (v.importo || 0), 0)])
    .filter(([, t]) => t > 0);
  const daCompletare = d.voci.filter((v) => v.importo === null || v.importo === undefined).length;
  const cella = { padding: '7px 6px', overflowWrap: 'anywhere', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const campo = { padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', color: 'inherit', font: 'inherit', fontSize: 14 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', alignItems: 'baseline' }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Totale {euro(d.totale)}</div>
        {perCategoria.map(([c, t]) => <span key={c} style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{c} {euro(t)}</span>)}
        {daCompletare > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: '#a6701c' }}>{daCompletare} da completare</span>}
      </div>
      {perPagatore.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13 }}>
          {perPagatore.map(([n, t]) => <span key={n}>Pagato da <strong>{n}</strong>: {euro(t)}</span>)}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
          <thead>
            <tr>
              {['Voce', 'Importo'].map((h) => (
                <th key={h} style={{ ...cella, textAlign: h === 'Importo' ? 'right' : 'left', color: 'var(--fg-muted)', fontWeight: 600, fontSize: 13 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.voci.map((v) => (
              <tr key={v.id} style={inModifica === v.id ? { background: 'var(--border)' } : undefined}>
                {/* Data, categoria, note e azioni stanno sotto la voce: sul telefono la riga entra tutta in larghezza (tester, 24/10/2026). */}
                <td style={cella}>
                  {v.voce}
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{v.data.split('-').reverse().join('/')} · {v.categoria} · pagata da {v.pagata_da || 'non indicato'}{v.note && ` · ${v.note}`}</div>
                  <div style={{ display: 'flex', gap: 4, marginLeft: -6 }}>
                    <button type="button" onClick={() => modifica(v)} style={{ background: 'none', border: 0, color: '#1a5fb4', cursor: 'pointer', fontSize: 13, padding: '8px 10px' }}>modifica</button>
                    <button type="button" onClick={() => elimina(v)} aria-label={`Togli ${v.voce}`} style={{ background: 'none', border: 0, color: '#b42318', cursor: 'pointer', fontSize: 13, padding: '8px 10px' }}>togli</button>
                  </div>
                </td>
                <td style={{ ...cella, textAlign: 'right', whiteSpace: v.importo == null ? 'normal' : 'nowrap', fontWeight: 600, color: v.importo == null ? '#a6701c' : 'inherit' }}>{v.importo == null ? 'da completare' : euro(v.importo)}</td>
              </tr>
            ))}
            {d.voci.length === 0 && <tr><td colSpan={2} style={{ ...cella, color: 'var(--fg-muted)' }}>Nessuna spesa ancora.</td></tr>}
          </tbody>
        </table>
      </div>
      <form onSubmit={salva} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} style={campo} aria-label="Data" />
        <input required placeholder="Voce (es. copia chiavi)" value={form.voce} onChange={(e) => setForm({ ...form, voce: e.target.value })} style={{ ...campo, flex: '1 1 200px' }} aria-label="Voce" />
        <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={campo} aria-label="Categoria">
          {d.categorie.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input inputMode="decimal" placeholder="Importo €" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} style={{ ...campo, width: 110 }} aria-label="Importo in euro" />
        <input list={`pagatori-${slug}`} placeholder="Pagata da" value={form.pagata_da} onChange={(e) => setForm({ ...form, pagata_da: e.target.value })} style={{ ...campo, width: 170 }} aria-label="Pagata da" />
        <datalist id={`pagatori-${slug}`}>{d.pagatori.map((n) => <option key={n} value={n} />)}</datalist>
        <input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={{ ...campo, flex: '1 1 160px' }} aria-label="Note" />
        <button type="submit" disabled={salvo} style={{ ...campo, background: '#1a5fb4', color: '#fff', border: 0, fontWeight: 600, cursor: 'pointer' }}>{inModifica ? 'Salva modifica' : 'Aggiungi'}</button>
        {inModifica && <button type="button" onClick={() => { setInModifica(null); setForm({ ...VUOTA, data: oggi() }); }} style={{ ...campo, cursor: 'pointer' }}>Annulla</button>}
      </form>
      {errore && <div style={{ color: '#b42318', fontSize: 13 }}>{errore}</div>}
    </div>
  );
}

function ProgettiHome() {
  const [progetti, setProgetti] = useState(null);
  const [errore, setErrore] = useState(null);
  const [aperto, setAperto] = useState(null);

  useEffect(() => {
    let vivo = true;
    fetch('/api/progetti', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!vivo) return;
        const lista = d.progetti || [];
        setProgetti(lista);
        setAperto(lista[0]?.slug || null);
      })
      .catch((e) => vivo && setErrore(e.message));
    return () => { vivo = false; };
  }, []);

  if (errore) return <div style={{ padding: '40px 48px', color: 'var(--fg-muted)' }}>Non riesco a leggere i progetti ({errore}).</div>;
  if (progetti === null) return <div style={{ padding: '40px 48px', color: 'var(--fg-muted)' }}>Caricamento…</div>;
  if (progetti.length === 0) return <Vuota titolo="Progetti" />;

  const corrente = progetti.find((p) => p.slug === aperto) || progetti[0];
  // Le colonne vengono dai dati (progetti.json): aggiungere un campo non richiede build.
  const CAMPI_SCHEDA = [...new Set(progetti.flatMap((p) => Object.keys(p.scheda || {})))];

  return (
    <div className="progetti-home" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ padding: '22px 24px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Scelta dell'immobile (Michael, 24/10/2026): tutto quello che segue è del progetto selezionato. */}
        <div role="tablist" aria-label="Immobile" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {progetti.map((p) => (
            <button
              key={p.slug}
              type="button"
              role="tab"
              aria-selected={p.slug === corrente.slug}
              onClick={() => setAperto(p.slug)}
              style={{
                padding: '8px 14px', borderRadius: 999, fontSize: 14, cursor: 'pointer', font: 'inherit',
                border: `1px solid ${p.slug === corrente.slug ? '#1a5fb4' : 'var(--border)'}`,
                background: p.slug === corrente.slug ? '#1a5fb4' : 'transparent',
                color: p.slug === corrente.slug ? '#fff' : 'inherit', fontWeight: p.slug === corrente.slug ? 600 : 400,
              }}
            >
              {p.titolo}
            </button>
          ))}
        </div>
        {/* Scheda standard: le stesse colonne per ogni operazione (Michael, 22/10/2026). */}
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, whiteSpace: 'nowrap' }}>
            <thead>
              <tr>
                {CAMPI_SCHEDA.map((c) => (
                  <th key={c} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--fg-muted)', fontWeight: 600 }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {progetti.map((p) => (
                <tr
                  key={p.slug}
                  onClick={() => setAperto(p.slug)}
                  style={{ cursor: 'pointer', background: p.slug === corrente.slug ? 'var(--border)' : 'transparent' }}
                >
                  {CAMPI_SCHEDA.map((c) => (
                    <td key={c} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{p.scheda?.[c] ?? '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Sezione key={`spese-${corrente.slug}`} titolo="Spese">
          <Spese slug={corrente.slug} />
        </Sezione>
        {/* Una sezione per pratica (Michael, 22/10/2026): il titolo dice la pratica, dentro contatore e documenti. */}
        {(corrente.documentazione || []).map((pr) => (
          <Sezione key={pr.pratica} titolo={<>{pr.titolo || pr.pratica}{pr.tu && <Tu />}</>}>
            {pr.contatore && (corrente.contatori || []).filter((c) => c.nome === pr.contatore).map((c) => <Contatore key={c.nome} c={c} />)}
            {pr.nota && <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 4 }}>{pr.nota}</div>}
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
              {pr.documenti.map((d) => (
                <li key={d.nome}>
                  {d.file ? <a href={`/progetti/${corrente.slug}/${d.file}`} target="_blank" rel="noreferrer" style={{ color: '#1a5fb4', textDecoration: 'underline', fontWeight: 500 }}>📎 {d.nome}</a> : d.nome}
                  <span style={{ fontSize: 12, marginLeft: 8, fontWeight: 700, color: d.stato === 'pronto' ? '#2f7d4f' : '#b42318' }}>{d.stato}</span>
                </li>
              ))}
            </ul>
          </Sezione>
        ))}
        {/* I contatori senza una pratica che li richiami restano in una sezione a parte. */}
        {(() => {
          const legati = new Set((corrente.documentazione || []).map((pr) => pr.contatore).filter(Boolean));
          const sciolti = (corrente.contatori || []).filter((c) => !legati.has(c.nome));
          return sciolti.length > 0 && (
            <Sezione titolo="Contatori">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {sciolti.map((c) => <Contatore key={c.nome} c={c} />)}
              </div>
            </Sezione>
          );
        })()}
        {corrente.difformita && <Difformita d={corrente.difformita} slug={corrente.slug} />}
        {(corrente.liste || []).map((l) => <Lista key={l.titolo} l={l} />)}
        {Array.isArray(corrente.elenco) && (
          <Sezione titolo="Cose da fare">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px 24px' }}>
              {corrente.elenco.map((g) => (
                <div key={g.fase}>
                  <div style={{ fontSize: 13, color: 'var(--fg-muted)', fontWeight: 600, margin: '6px 0 2px' }}>{g.fase}</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
                    {g.punti.map((x) => {
                      const v = typeof x === 'string' ? { testo: x } : x;
                      return (
                        <li key={v.testo} style={v.fatto ? { color: '#2f7d4f', listStyle: '"✓  "' } : undefined}>
                          {v.testo}{v.fatto && v.data && <span style={{ fontSize: 12, marginLeft: 6 }}>· fatto il {v.data}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </Sezione>
        )}
        {(corrente.bpmn || corrente.flusso) && (
          <Sezione titolo="Diagramma del flusso">
            {corrente.bpmn ? <Bpmn xml={corrente.bpmn} /> : <Flusso sorgente={corrente.flusso} id={corrente.slug} />}
          </Sezione>
        )}
        {corrente.documento && (
          <Sezione titolo="Piano operativo">
            <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 8 }}>
              <a href={corrente.documento} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>apri a tutto schermo</a>
            </div>
            <iframe
              title={`Piano operativo — ${corrente.titolo}`}
              src={corrente.documento}
              style={{ width: '100%', minHeight: 'calc(100vh - 230px)', border: 0, borderTop: '1px solid var(--border)', background: 'transparent' }}
            />
          </Sezione>
        )}
      </div>
    </div>
  );
}

// La logica di Michael: elenco puntato letto da algoritmo.json (si aggiorna senza build).
function Algoritmo() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch('/api/progetti/algoritmo', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then(setD).catch(() => setD({ punti: [] }));
  }, []);
  if (!d) return <div style={{ padding: '40px 48px', color: 'var(--fg-muted)' }}>Caricamento…</div>;
  const punti = d.punti || [];
  return (
    <div style={{ padding: '40px 48px', maxWidth: 820 }}>
      <h1 style={{ fontSize: 24, margin: '0 0 6px' }}>Algoritmo</h1>
      {d.aggiornato && <p style={{ color: 'var(--fg-muted)', margin: '0 0 18px', fontSize: 13 }}>Aggiornato il {d.aggiornato}</p>}
      {punti.length === 0
        ? <p style={{ color: 'var(--fg-muted)' }}>Ancora vuoto: Michael lo detta.</p>
        : <ul style={{ lineHeight: 1.6, paddingLeft: 22 }}>{punti.map((p, i) => <li key={i} style={{ margin: '6px 0' }}>{p}</li>)}</ul>}
    </div>
  );
}

// Knowledge: una voce per argomento, letta da knowledge.json (si aggiorna senza build).
function Knowledge() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch('/api/progetti/knowledge', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then(setD).catch(() => setD({ voci: [] }));
  }, []);
  if (!d) return <div style={{ padding: '40px 48px', color: 'var(--fg-muted)' }}>Caricamento…</div>;
  const voci = [...(d.voci || [])].sort((a, b) => a.titolo.localeCompare(b.titolo, 'it'));
  return (
    <div style={{ padding: '40px 48px', maxWidth: 860 }}>
      <h1 style={{ fontSize: 24, margin: '0 0 18px' }}>Knowledge</h1>
      {voci.length === 0 && <p style={{ color: 'var(--fg-muted)' }}>Ancora vuota.</p>}
      {voci.map((v) => (
        <details key={v.titolo} open style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px', marginBottom: 10 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 16 }}>
            {v.titolo}{v.aggiornato && <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--fg-muted)', marginLeft: 10 }}>aggiornata il {v.aggiornato}</span>}
          </summary>
          <ul style={{ lineHeight: 1.6, paddingLeft: 22, margin: '8px 0 4px' }}>{(v.punti || []).map((p, i) => (typeof p === 'string'
            ? <li key={i} style={{ margin: '4px 0' }}>{p}</li>
            : (
              <li key={i} style={{ margin: '4px 0', listStyle: 'none', marginLeft: -22 }}>
                {p.testo && <div style={{ marginBottom: 6 }}>{p.testo}</div>}
                {p.img && <img src={p.img} alt={p.testo || ''} style={{ display: 'block', maxWidth: 'min(100%, 420px)', borderRadius: 8, border: '1px solid var(--border)' }} />}
              </li>
            )))}</ul>
        </details>
      ))}
    </div>
  );
}

// Video: tutti i video dei progetti (campo «video» di progetti.json), serviti in privato.
function Video() {
  const [progetti, setProgetti] = useState(null);
  useEffect(() => {
    fetch('/api/progetti', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then((d) => setProgetti(d.progetti || [])).catch(() => setProgetti([]));
  }, []);
  if (!progetti) return <div style={{ padding: '40px 48px', color: 'var(--fg-muted)' }}>Caricamento…</div>;
  const conVideo = progetti.filter((p) => Array.isArray(p.video) && p.video.length);
  return (
    <div style={{ padding: '32px 40px' }}>
      <h1 style={{ fontSize: 24, margin: '0 0 18px' }}>Video</h1>
      {conVideo.length === 0 && <p style={{ color: 'var(--fg-muted)' }}>Nessun video.</p>}
      {conVideo.map((p) => (
        <div key={p.slug} style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 10px' }}>{p.titolo}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
            {p.video.map((v) => (
              <figure key={v.file} style={{ margin: 0, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <video src={`/progetti/${p.slug}/${v.file}`} controls preload="metadata" playsInline style={{ display: 'block', width: '100%', aspectRatio: '9/16', background: '#000' }} />
                <figcaption style={{ padding: '8px 10px', fontSize: 13 }}>
                  <strong>{v.titolo}</strong>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{v.durata} · {v.data}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProgettiShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const voce = VOCI_PROGETTI.find((v) => pathname === `/app/${v.path}`);
  if (!voce) return <Navigate to="/app/progetti" replace />;
  return (
    <div className="progetti-scocca" style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Sul telefono il menu va in alto, in riga, invece di prendersi metà schermo. */}
      <style>{`@media (max-width: 700px){
        .progetti-scocca{flex-direction:column}
        .progetti-scocca aside{width:auto!important;flex-direction:row!important;flex-wrap:wrap;align-items:center;border-right:0!important;border-bottom:1px solid var(--border);padding:12px!important}
        .progetti-scocca aside .titolo{padding:0 10px 0 4px!important;width:100%}
        .progetti-scocca aside .utente{margin-top:0!important;margin-left:auto}
        .progetti-scocca main > div{padding:24px 16px!important}
        .progetti-scocca .progetti-home > div{padding:14px 12px 10px!important}
      }`}</style>
      <aside style={{ width: 220, borderRight: '1px solid var(--border)', padding: '24px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="titolo" style={{ fontWeight: 600, fontSize: 15, padding: '0 10px 18px' }}>Progetti</div>
        {VOCI_PROGETTI.map((v) => (
          <NavLink
            key={v.path}
            to={`/app/${v.path}`}
            style={({ isActive }) => ({
              padding: '9px 10px', borderRadius: 8, textDecoration: 'none', fontSize: 14,
              color: 'inherit', background: isActive ? 'var(--border)' : 'transparent',
            })}
          >
            {v.label}
          </NavLink>
        ))}
        <div className="utente" style={{ marginTop: 'auto', padding: '0 10px', fontSize: 13, color: 'var(--fg-muted)' }}>
          <div style={{ marginBottom: 8 }}>{user?.name}</div>
          <button type="button" onClick={logout} style={{ background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>
            Esci
          </button>
        </div>
      </aside>
      <main style={{ flex: 1, minWidth: 0 }}>
        {voce.path === 'progetti' ? <ProgettiHome /> : voce.path === 'algoritmo' ? <Algoritmo /> : voce.path === 'knowledge' ? <Knowledge /> : voce.path === 'video' ? <Video /> : <Vuota titolo={voce.label} />}
      </main>
    </div>
  );
}
